/**
 * @fileoverview A group that is a photo of seed packets. Two cards come from one scan (B137, design rev 5), and this file
 * is the one place that chooses: a bed's own doors ("Add a new plant here" on a bed, whose scan the server marks
 * `bedNamed`, and Beds' "Add a bed", the sheet's `newBed`) get today's plant card below, planted into that bed; every other
 * door (Today's "A photo of anything", the Plants and Seasons add pop-ups, the Photos page) gets the Seed vault card
 * (PacketKeep). The app decides nothing about the bed: `bedNamed` is the server's, `newBed` the sheet's own prop.
 *
 * Today's plant card: instead of "What is this?", "Seed packets · 4 plants" and the
 * packets read off it, each a row to tick, then the bed, the starting stage and one button, "Add 4 plants to Bed A".
 *
 * The photo's guess said it was seed packets, and the tray had the packet reader read it (POST /api/tray/packets);
 * while it reads, the card says so. Each packet is a row (D78 rev 2 §3, PacketRow through lib/usePacketRows, the same
 * rows as the review sheet): tick · its own packet cut out of the photo · its name, "New variety" for one nobody has
 * yet · ›. The tick is its own target; the rest of the row opens the row's change dialog (RowChangeDialog): what the
 * packet says, Grow's suggestion for a packet it could not place (one Yes), the plant fields with their search, a
 * new variety's name, a bed of its own (rev 2 §2: a seed order is for the whole garden), Done and "Remove this
 * packet". A packet Grow could not place says "Which plant is this?" and cannot be ticked until it has a plant; the
 * line above the button counts them ("1 packet still needs a plant."), so none is dropped silently.
 *
 * Gone since D78 (owner, 2026-10-22): "Check each packet" (the trip into the review sheet; every row opens its own
 * dialog now) and "These aren't seed packets" (the misread case is the group's ⋯ menu, "Ask about it as a plant", on
 * PileCard). The review sheet's lines came onto the card instead: "Starting at Planning · Change" (StartingStage) and
 * "First to start: Carrot, Feb 18" above the button (lib/useFirstToStart, D40), while the stage is Planning.
 *
 * The bed starts on the one the photo's label names, else the one the photo was added from, else a new bed by the
 * label's name, else their first bed; added from Beds' "Take or upload a photo" (newBed, D78 P2) it starts on a new
 * bed, named from the label when the photo has one. The heading counts the plants; the button names every place
 * they go ("Add 4 plants: 3 to Back bed, 1 to Herbs", lib/bedPlaces).
 *
 * Adding files the packets the way every packet scan is filed (POST /api/packets/confirm, each plant keeping its own
 * packet cut out of the photo, each row's own bed with it), and the photo leaves the tray.
 *
 * On Free, with more packets ticked than Free has room for, the button offers what fits and the rest with Grow More
 * ("Add 2 now" and "Add all 4 with Grow More", FreeRoomChoice); the server adds the first ones in the list, up to the
 * room (upToRoom).
 *
 * Where: PileCard, for a photo the guess saw seed packets in (TrayPiles: the Photos page and the "Save" sheet).
 * API: POST /packets/confirm (confirmPackets, with the photo's trayIds in the scan so it leaves the tray, and
 * upToRoom); the rows' (the catalogue, the suggestion's POST /plants/identify and POST /plants/own); the first to
 * start's (useFirstToStart).
 * Settings: the server's limits.LOCATION_NAME_MAX (lib/growSettings); Free's room (useFreeRoom).
 * Words (en/es): packets.* (title, titleCount, titleCountOne, reading, bed, bedName, newBed, aNewBed, labelSays,
 * why.bed, why.plantOne, why.plants, saveFailed), tray.packets.noneTicked; the rows', the dialog's and StartingStage's own; FreeRoomChoice's
 * billing.free.*.
 * A failed add says why (lib/errorMessage): the server's refusal, or for a failure on our side (a 500, no
 * connection; D31) the standard sentence, reported to Sentry, with the support address as a link; else
 * packets.saveFailed.
 *
 * @module PacketPile
 */

import React from 'react';
import { Button } from '@/shared/components/ui/button';
import { AddCancelRow } from './AddCancelRow';
import { PacketKeep } from './PacketKeep';
import { Input } from '@/shared/components/ui/input';
import { NativeSelect } from '@/shared/components/ui/native-select';
import Spinner from '@/shared/components/ui/Spinner';
import { GrowLoader } from '@/shared/components/ui/GrowLoader';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { useStagesContext } from '@/shared/hooks/useStagesContext';
import { selectableStages } from '@/shared/lib/stages';
import { confirmPackets, type KeepResult, type PacketResult, type PacketScan } from '@/shared/api/packets';
import type { BedName } from '@/shared/api/locations';
import { useGrowLimits } from '@/shared/lib/growSettings';
import { FreeRoomChoice } from '@/features/billing/components/FreeRoom';
import { useFreeRoom } from '@/features/billing/lib/useFreeRoom';
import { usePacketRows } from '@/features/packets/lib/usePacketRows';
import { plain, rowItem } from '@/features/packets/lib/packetRows';
import { useFirstToStart } from '@/features/packets/lib/useFirstToStart';
import { addLabelFor, bedPlaces } from '@/features/packets/lib/bedPlaces';
import { startingStage, atPlanningStage } from '@/features/packets/lib/startingChoices';
import { StartingStage } from '@/features/packets/components/StartingStage';

/** What the card's packet part is given. */
interface PacketPileProps {
  /** What the packet reader read; null while it is still reading. */
  scan: PacketScan | null;
  /** The live beds. */
  locations: BedName[];
  /** True while the card is busy elsewhere (its "Ask about it as a plant" being sent). */
  busy: boolean;
  /** For the fields' ids. */
  idBase: string;
  /** The packets were filed into a bed (today's card): what was added, and to which bed. */
  onFiled: (result: PacketResult) => void;
  /** The packets were kept in the Seed vault (the vault card): the vault and the seeds kept. */
  onKept: (result: KeepResult) => void;

  /** Added from Beds' photo row (D78 P2): start on a new bed, named from the label when there is one. */
  newBed?: boolean;
  /** Cancel, beside the Add button: what the photo's × does. */
  onCancel?: () => void;
}

/** The "new bed" choice in the Bed list. */
const NEW_BED = 'new';

/**
 * The bed the card starts on, as the read said it: one of theirs, or a new one by the label's name; null to start on
 * their first bed. Added from Beds, a new bed, named from the label when there is one.
 *
 * @param scan - What was read.
 * @param newBed - Added from Beds' photo row.
 * @returns The Bed list's choice and a new bed's name, or null.
 */
const bedOfScan = (scan: PacketScan, newBed: boolean): { choice: string; name: string } | null => {
  if (newBed) return { choice: NEW_BED, name: scan.bed?.name ?? scan.label ?? '' };
  if (scan.bed?.locationId) return { choice: String(scan.bed.locationId), name: '' };
  if (scan.bed?.isNew) return { choice: NEW_BED, name: scan.bed.name };
  return null;
};

/**
 * The packet part of a group's card: reading, or the packets read as the Seed vault card, or (a bed's own door) with
 * their bed and button.
 *
 * @param props - PacketPileProps.
 */
export const PacketPile: React.FC<PacketPileProps> = ({ scan, onKept, onCancel, ...rest }) => {
  const { reactTranslate: t } = useTranslation();
  if (!scan) {
    return (
      <>
        <h3 className="font-semibold font-body">{t('packets.title')}</h3>
        <GrowLoader variant="block" label={t('packets.reading')} />
      </>
    );
  }
  // The one rule: the server's bedNamed (the photo was added from a bed), or Beds' "Add a bed" (newBed), is a plant card.
  if (scan.bedNamed || rest.newBed) return <PacketChoice scan={scan} onCancel={onCancel} {...rest} />;
  return <PacketKeep scan={scan} locations={rest.locations} busy={rest.busy} idBase={rest.idBase} onKept={onKept} onCancel={onCancel} />;
};

/**
 * The packets read, each a row to tick or change, the bed, the starting stage, and the button that adds them.
 *
 * @param props - PacketPileProps, with the scan read.
 */
const PacketChoice: React.FC<Omit<PacketPileProps, 'scan' | 'onKept'> & { scan: PacketScan }> = ({
  scan, locations, busy, idBase, onFiled, newBed: startNew = false, onCancel,
}) => {
  const { reactTranslate: t } = useTranslation();
  const limits = useGrowLimits();
  const { stages } = useStagesContext();
  /** The stages a packet's plant may start at: Planning first (its seeds are not sown yet), "Not sure yet" last. */
  const stageList = React.useMemo(() => selectableStages(stages, { planning: true }), [stages]);
  const [stageId, setStageId] = React.useState<number | null>(() => startingStage(stageList));
  /** The stage no longer follows the list: it was started with the stages in, they came in, or the gardener chose. */
  const stageSettled = React.useRef(stageList.length > 0);
  React.useEffect(() => {
    if (stageSettled.current || stageList.length === 0) return;
    stageSettled.current = true;
    setStageId(startingStage(stageList));
  }, [stageList]);

  // The bed the read named (or a new one, from Beds); null until the gardener chooses, which starts on their first bed.
  const [bedChoice, setBedChoice] = React.useState<string | null>(() => bedOfScan(scan, startNew)?.choice ?? null);
  const [bedName, setBedName] = React.useState(() => bedOfScan(scan, startNew)?.name ?? '');
  const [saving, setSaving] = React.useState(false);
  // Why the last add failed (lib/errorMessage's sentence), or null.
  const [failed, setFailed] = React.useState<string | null>(null);

  const choice = bedChoice ?? (locations[0] ? String(locations[0].id) : NEW_BED);
  const newBed = choice === NEW_BED;
  const bedLabel = newBed ? bedName.trim() : locations.find((bed) => String(bed.id) === choice)?.name ?? '';
  const off = saving || busy;
  const { index, shown, kept, unplaced, list, dialog } = usePacketRows({
    scan, sharedBed: bedLabel || t('packets.aNewBed'), beds: locations, disabled: off, idBase,
  });
  /** The bed every row without its own goes to, as POST /packets/confirm names it. */
  const sharedTarget: { locationId: number } | { newBedName: string } = newBed ? { newBedName: bedLabel } : { locationId: Number(choice) };
  const blocked = kept.length === 0 ? t('tray.packets.noneTicked')
    : !bedLabel || kept.some((row) => row.bed?.kind === 'new' && !row.bed.name.trim()) ? t('packets.why.bed') : null;
  // The label names a bed other than the one chosen: said, as the sheet says it.
  const labelElsewhere = scan.label && plain(scan.label) !== plain(bedLabel);
  // On Free: more ticked than there is room for, so the button offers what fits.
  const free = useFreeRoom();
  const overRoom = free.limited && kept.length > free.room;
  const atPlanning = atPlanningStage(stageList, stageId);
  const firstLine = useFirstToStart(kept, atPlanning);

  /**
   * Files the ticked packets, each in its bed; the photo leaves the tray with them.
   *
   * @param upToRoom - On Free, add what fits and leave the rest.
   */
  const add = async (upToRoom = false): Promise<void> => {
    if (blocked) return;
    setSaving(true);
    setFailed(null);
    try {
      onFiled(await confirmPackets(scan, sharedTarget, stageId, kept.map((row) => rowItem(index, row, sharedTarget)), upToRoom));
    } catch (error) {
      setFailed(errorMessage(error, t('packets.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const places = bedPlaces(kept.map((row) => row.bed), { id: newBed ? null : Number(choice), newName: newBed ? bedLabel : null }, locations, t);
  const addLabel = addLabelFor(places, kept.length, bedLabel, t);

  return (
    <>
      {/* "Seed packets · 4 plants": the card counts its plants (rev 2 §2). */}
      <h3 className="font-semibold font-body">
        {t(shown.length === 1 ? 'packets.titleCountOne' : 'packets.titleCount', { count: shown.length })}
      </h3>

      {list}

      <label className="block text-xs text-muted-foreground" htmlFor={`${idBase}-packets-bed`}>
        {t('packets.bed')}
        <NativeSelect
          id={`${idBase}-packets-bed`}
          className="mt-1 text-sm text-foreground"
          value={choice}
          onChange={(event) => setBedChoice(event.target.value)}
          disabled={off}
        >
          {locations.map((bed) => <option key={bed.id} value={bed.id}>{bed.name}</option>)}
          <option value={NEW_BED}>{t('packets.newBed')}</option>
        </NativeSelect>
      </label>
      {newBed && (
        <label className="block text-xs text-muted-foreground" htmlFor={`${idBase}-packets-bed-name`}>
          {t('packets.bedName')}
          <Input
            id={`${idBase}-packets-bed-name`}
            className="mt-1 text-sm"
            value={bedName}
            onChange={(event) => setBedName(event.target.value)}
            maxLength={limits.LOCATION_NAME_MAX}
            disabled={off}
          />
        </label>
      )}
      {labelElsewhere && <p className="-mt-1 text-xs text-muted-foreground">{t('packets.labelSays', { label: scan.label ?? '' })}</p>}

      {/* "Starting at Planning · Change", from the review sheet, on the card (rev 2 §3). */}
      <StartingStage stages={stageList} stageId={stageId} onChange={(id) => { stageSettled.current = true; setStageId(id); }} id={`${idBase}-packets-stage`} disabled={off} />

      {failed && <p className="text-sm font-medium text-destructive" role="alert"><FailureText text={failed} /></p>}
      {/* A packet Grow could not place is not filed until it has a plant: said, so it is not dropped silently. */}
      {unplaced > 0 && <p className="text-center text-xs text-muted-foreground" role="status">{t(unplaced === 1 ? 'packets.why.plantOne' : 'packets.why.plants', { count: unplaced })}</p>}
      {blocked && <p className="text-center text-xs text-muted-foreground" role="status">{blocked}</p>}
      {/* "First to start: Carrot, Feb 18", while the stage is Planning and nothing still needs settling. */}
      {!blocked && firstLine && <p className="text-center text-xs text-muted-foreground">{firstLine}</p>}
      {overRoom ? (
        <FreeRoomChoice count={kept.length} free={free} onAdd={() => void add(true)} disabled={off || Boolean(blocked)} saving={saving} />
      ) : (
        <AddCancelRow onCancel={onCancel} disabled={off}>
          <Button className="w-full" size="lg" onClick={() => void add()} disabled={off || Boolean(blocked)}>
            {saving ? <Spinner size={16} color="currentColor" /> : addLabel}
          </Button>
        </AddCancelRow>
      )}

      {dialog}
    </>
  );
};
